import type { TopicLevel } from "@sikilesi/protocol";
import { levelLabel } from "./tr";

/** X counts every link as 23 characters (t.co) and the post limit is 280. */
export const X_LIMIT = 280;
export const X_URL_WEIGHT = 23;

/**
 * Weight of one code point in X's post counter: Latin (incl. Turkish ğ ş ı İ),
 * Greek, Cyrillic… count 1; CJK, emoji and the rest count 2.
 */
export function xWeight(cp: number): number {
  if (cp <= 0x10ff) return 1;
  if ((cp >= 0x2000 && cp <= 0x200d) || (cp >= 0x2010 && cp <= 0x201f) || (cp >= 0x2032 && cp <= 0x2037)) return 1;
  return 2;
}

export const xLength = (text: string): number => Array.from(text).reduce((sum, ch) => sum + xWeight(ch.codePointAt(0) ?? 0), 0);

/** Cut `text` so its X weight is at most `max`, ending with "…" when shortened. */
export function trimForX(text: string, max: number): string {
  if (xLength(text) <= max) return text;
  let out = "";
  let used = xWeight(0x2026); // the ellipsis (X counts it double)
  for (const ch of Array.from(text)) {
    const w = xWeight(ch.codePointAt(0) ?? 0);
    if (used + w > max) break;
    out += ch;
    used += w;
  }
  return `${out.trimEnd()}…`;
}

/** Post text: the finding's title + its stamp level, fitted with the link into 280. */
export function buildShareText(title: string, level: TopicLevel): string {
  const suffix = ` — ${levelLabel(level)} damgası`;
  const budget = X_LIMIT - X_URL_WEIGHT - 1 - xLength(suffix); // 1 = space before the link
  return `${trimForX(title, budget)}${suffix}`;
}

export function xIntentUrl(text: string, url: string): string {
  const params = new URLSearchParams({ text, url });
  return `https://x.com/intent/post?${params.toString()}`;
}

/** Web Share API on touch devices; desktop gets the X intent + copy link menu. */
export function canNativeShare(nav: Navigator = navigator, win: Window = window): boolean {
  return typeof nav.share === "function" && win.matchMedia("(pointer: coarse)").matches;
}

export async function copyText(text: string, nav: Navigator = navigator): Promise<boolean> {
  try {
    await nav.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
